import type {
  ObjectWorkspaceObject,
  ObjectWorkspacePatientSessionsState,
} from '@heidi/agents';
import { getDisplayableSessionGist } from '@heidi/session/contracts';
import type { PagedLinkedSession } from '@heidi/session/data';

interface ResolvePatientSessionsStateOptions {
  object: ObjectWorkspaceObject;
  sessions: readonly PagedLinkedSession[] | undefined;
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onShowMore: () => void;
  untitledTitle: string;
  formatStartTime: (time: string) => string;
}

export function resolvePatientSessionsState({
  object,
  sessions,
  isLoading,
  isError,
  hasMore,
  isLoadingMore,
  onShowMore,
  untitledTitle,
  formatStartTime,
}: ResolvePatientSessionsStateOptions): ObjectWorkspacePatientSessionsState | undefined {
  if (object.kind !== 'patient') return undefined;
  // A patient opened from a session with no linked profile has nothing to list.
  if (!object.patientProfileId) return { status: 'ready', sessions: [] };
  if (isLoading) return { status: 'loading' };
  if (isError) return { status: 'error' };

  return {
    status: 'ready',
    sessions: (sessions ?? []).map((session) => {
      const startTime = session.created_time_local || session.created_at;
      return {
        id: session.session_id,
        kind: 'session' as const,
        title: getDisplayableSessionGist(session.session_name) ?? untitledTitle,
        ...(startTime ? { occurredAtLabel: formatStartTime(startTime) } : {}),
      };
    }),
    hasMore,
    isLoadingMore,
    onShowMore,
  };
}
